import React, { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { MessageSquarePlus, SendHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { useUser } from '@/store/useUser';
import { normalizePolicyString, policyMatches } from '@/utils/policies';

const CREATE_SUPPORT_TICKET = gql`
  mutation CreateSupportTicketFromFooter($message: String!) {
    createSupportTicket(message: $message) {
      id
    }
  }
`;

const SUPPORT_TICKET_POLICY = 'user:authenticated~full';
const SUPPORT_TICKET_PLACEHOLDER = 'Tell us what happened, what page you were on, and what you were trying to do.';

const canCreateSupportTicket = (user: any) => {
  const policies = Array.isArray(user?.policies) ? user.policies : [];
  const normalized = policies
    .map((policy: unknown) => normalizePolicyString(policy))
    .filter(Boolean);
  return normalized.some((policy: string) => policyMatches(policy, SUPPORT_TICKET_POLICY));
};

export const Footer = () => {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [createSupportTicket, { loading }] = useMutation(CREATE_SUPPORT_TICKET);

  const showSupportTicketControl = canCreateSupportTicket(user);

  useEffect(() => {
    const handleOpenSupport = () => setIsOpen(true);
    window.addEventListener('footer-support:open', handleOpenSupport);
    return () => window.removeEventListener('footer-support:open', handleOpenSupport);
  }, []);

  if (!showSupportTicketControl) return null;

  const submitTicket = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error('Please enter a message before sending.');
      return;
    }

    try {
      const result = await createSupportTicket({
        variables: {
          message: trimmed,
        },
      });
      const ticketId = result?.data?.createSupportTicket?.id;
      setMessage('');
      setIsOpen(false);
      if (ticketId) {
        toast.success(`Support ticket #${ticketId} created.`);
        return;
      }
      toast.success('Support ticket created.');
    } catch (error: any) {
      const messageText = error?.message || 'Failed to create support ticket';
      toast.error(messageText);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2">
      {isOpen && (
        <div className="w-[32rem] max-w-[96vw] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-200 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Contact Support</div>
                <p className="mt-1 text-xs leading-5 text-slate-600">
                  Tell us what happened, where it happened, and what you were trying to do.
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setIsOpen(false)}
                aria-label="Close support panel"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-3 px-4 py-4">
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-[0.04em] text-slate-500">What Happened</div>
              <Textarea
                id="footer-support-ticket-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder={SUPPORT_TICKET_PLACEHOLDER}
                rows={6}
                disabled={loading}
              />
              <div className="text-xs leading-5 text-slate-500">
                Support will follow up by email after you send this.
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
              <Button
                type="button"
                className="gap-2"
                onClick={submitTicket}
                disabled={loading || message.trim().length === 0}
              >
                <SendHorizontal className="h-4 w-4" />
                {loading ? 'Sending...' : 'Send'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={loading}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button
        id="footer-support-ticket-toggle"
        type="button"
        className="h-11 rounded-full px-4 shadow-lg"
        onClick={() => setIsOpen((current) => !current)}
      >
        <MessageSquarePlus className="mr-2 h-4 w-4" />
        Support
      </Button>
    </div>
  );
};
